import { Injectable } from '@angular/core';
import { UrlGeneratorService } from '@app/core/i18n';
import { Picture, Price, Product, ProductMeasurement, User } from '@vestiaire/api-sdk-angular';
import { ImagesService } from '@app/shared/components/images/services/images.service';

@Injectable()
export class ProductMicrodatasService {
  public schema: {};

  constructor(private urlGenerator: UrlGeneratorService) {}

  private computeImagePath(pictureData: Picture[], productPath: string) {
    return pictureData.map(p => {
      const url = ImagesService.improveSEOProductPathImg(p.path, productPath);
      return ImagesService.buildUrl(`${url}`);
    });
  }

  private computeMeasurment(measurmentData: Array<ProductMeasurement>, unit: String) {
    const authorizedMeasurments: Array<string> = ['height', 'width', 'depth'];

    Object.keys(measurmentData).forEach(itemKey => {
      if (measurmentData[itemKey].name) {
        if (authorizedMeasurments.includes(measurmentData[itemKey].name.toLowerCase())) {
          this.schema[measurmentData[itemKey].name] = {
            '@type': 'QuantitativeValue',
            value: measurmentData[itemKey].value
          };
          if (unit) {
            this.schema[measurmentData[itemKey].name].unitCode = unit;
          }
        }
      }
    });
  }

  private computeDescription(description: String) {
    return description
      .split(' ')
      .slice(0, 50)
      .join(' ');
  }

  private populateProductModel(productData: Product) {
    const modelData = productData.model;
    if (modelData.name) {
      return {
        '@type': 'ProductModel',
        name: modelData.name,
        offers: {
          '@type': 'Offer',
          priceCurrency: productData.price.currency,
          itemCondition: 'https://schema.org/UsedCondition',
          price: this.formatPrice(productData.price),
          seller: this.populateSeller(productData.seller),
          availability: !productData.available ? 'https://schema.org/SoldOut' : 'https://schema.org/InStock'
        }
      };
    }
    return '';
  }

  private formatPrice(priceData: Price): String {
    const price: number = priceData.cents / 100;
    return price.toString();
  }

  private populateSeller(sellerData: User) {
    const sellerDataDataFormated: object = {};

    if (sellerData.badges.includes('pro')) {
      sellerDataDataFormated['@type'] = 'Organization';
    } else {
      sellerDataDataFormated['@type'] = 'Person';
    }

    sellerDataDataFormated['name'] = sellerData.firstname;

    return sellerDataDataFormated;
  }

  public getDatas(productData: Product): any {
    // Required data
    this.schema = {
      '@context': 'https://schema.org/',
      '@type': 'Product',
      url: this.urlGenerator.generateUrl(productData.path),
      name: productData.name,
      sku: productData.id,
      brand: {
        '@type': 'Thing',
        name: productData.brand.name
      },
      offers: {
        '@type': 'Offer',
        priceCurrency: productData.price.currency,
        itemCondition: 'https://schema.org/UsedCondition',
        availability: 'https://schema.org/InStock',
        shippingDetails: {
          '@type': 'OfferShippingDetails',
          shippingRate: {
            value: 999,
            currency: productData.price.currency
          }
        }
      }
    };

    const maxDuties = 18000;
    const maxAuth = 44.99;

    this.schema['offers'].price = +this.formatPrice(productData.price) + maxDuties + maxAuth;

    this.schema['offers'].seller = this.populateSeller(productData.seller);

    if (!productData.available) {
      this.schema['offers'].availability = 'https://schema.org/SoldOut';
    }

    // Non required data
    if (productData.pictures && productData.path) {
      this.schema['image'] = this.computeImagePath(productData.pictures, productData.path);
    }

    if (productData.description) {
      this.schema['description'] = this.computeDescription(productData.description);
    }

    if (productData.category && productData.category.localizedName) {
      this.schema['category'] = productData.category.localizedName;
    }

    if (productData.color && productData.color.localizedName) {
      this.schema['color'] = productData.color.localizedName;
    }

    if (productData.material && productData.material.localizedName) {
      this.schema['material'] = productData.material.localizedName;
    }

    if (productData.measurements && productData.unit) {
      this.computeMeasurment(productData.measurements, productData.unit);
    }

    if (productData.model?.name) {
      this.schema['model'] = this.populateProductModel(productData);
    }

    return this.schema;
  }
}
